import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import { Box, Button, Drawer, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Permission } from '@/enums';

import { config } from '../../../config';
import useComponentVisible from '../../../hooks/useComponentVisible';
import { useAppDispatch } from '../../../store';
import { clientSettingsSelector, setThemeName, toggleSidebar } from '../../../store/slices/clientsettings';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { userSelector } from '../../../store/slices/user';
import type { Theme } from '../../../themes';
import { themes, themeSelector } from '../../../themes';
import FuelAutoComplete from '../../fuel-autocomplete';
import UserDetailsModal from '../../UserDetailsModal/UserDetailsModal';

class IProps {
  handleLogout: () => void;
}

export const UserNav: FC<IProps> = ({ handleLogout }: IProps) => {
  const { appVersion } = config;
  const [showThemeSelector, setShowThemeSelector] = useState<boolean>(false);
  const selectedTheme = themeSelector.getTheme();
  const { user } = useSelector(userSelector);
  const { fuelUserAccessData, permissions } = useSelector(fuelUserAccessSliceSelector);
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [themeList, setThemeList] = useState([]);
  const { isToggleSidebarOpen } = useSelector(clientSettingsSelector);
  const flags = useFlags();

  const logout = (event) => {
    setShowThemeSelector(false);
    dispatch(toggleSidebar());
    handleLogout();
    event.preventDefault();
  };
  const handleShowThemeSelector = (event) => {
    event.preventDefault();
    setShowThemeSelector(true);
  };

  const handleCloseThemeSelector = (event) => {
    event.preventDefault();
    setShowThemeSelector(false);
  };

  const handleThemeChange = (theme: Theme) => {
    event.preventDefault();
    themeSelector.setTheme(theme.name);
    dispatch(setThemeName(theme.name));
  };

  const showUserModal = async () => {
    // console.log('showUserModal', { fuelUserAccessData, permissions });
    const { userId } = fuelUserAccessData;
    setUserId(userId);
    setOpenUserModal(true);
  };

  const excludedThemes = useMemo(() => {
    if (flags['enable-view-nexa-new-ui-design']) {
      return ['light-theme', 'dark-theme'];
    }
    if (permissions?.has(Permission.OUSuperAdmin)) {
      return [];
    }
    return ['light-theme-viewnexa', 'dark-theme-viewnexa'];
  }, [flags, permissions]);

  const list = useMemo(() => themes.filter((theme) => !excludedThemes.includes(theme.name)), [themes, excludedThemes]);

  useEffect(() => {
    setThemeList(list);
  }, [list]);
  return (
    <div className="user-nav" ref={ref}>
      <Drawer className="theme-selector-drawer" anchor="left" open={showThemeSelector} onClose={handleCloseThemeSelector}>
        <Box sx={{ p: 1 }}>
          <Typography className="theme-change-title" sx={{ mt: 2, p: 2 }}>
            Theme Change
          </Typography>

          <FuelAutoComplete
            items={themeList}
            itemText="text"
            labelText="name"
            placeHolderText="Select"
            controlWidth={350}
            multiSelect={false}
            labelPaddingTop="-5px"
            onChange={handleThemeChange}
            defaultValue={selectedTheme}
          />
        </Box>
      </Drawer>

      {isComponentVisible && (
        <div>
          <div className="menu-mask" onClick={() => setIsComponentVisible(false)} />
          <li className="user-nav-menu">
            <ul>
              <li onClick={logout}>
                <a className="text-link">Logout</a>
              </li>
              <li onClick={showUserModal}>
                <a className="text-link">Profile</a>
              </li>
              <li onClick={handleShowThemeSelector}>
                <a className="text-link">Theme</a>
              </li>
              <li className="disabled">FuelUI v {appVersion}</li>
            </ul>
          </li>
        </div>
      )}
      {openUserModal && (
        <UserDetailsModal
          open={openUserModal}
          onClose={() => setOpenUserModal(false)}
          isExisting
          userId={userId}
          isProfile
          permissions={permissions}
        />
      )}
      <div className="user-nav-label" onClick={() => setIsComponentVisible(!isComponentVisible)}>
        {user && (
          <Button
            variant="text"
            endIcon={
              isComponentVisible ? (
                <ArrowDropUpOutlinedIcon className="dropdownIcon" />
              ) : (
                <ArrowDropDownIcon className="dropdownIcon" />
              )
            }
            onClick={() => setIsComponentVisible(!isComponentVisible)}
          >
            {user.given_name} {user.family_name}{' '}
          </Button>
        )}
      </div>
    </div>
  );
};
